import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BrainCircuit, WandSparkles, X } from 'lucide-react';
import type { JarvisRailAppContext, JarvisRailContext } from './jarvisTypes';
import { getJarvisQuickPrompts } from './jarvisPrompts';

interface JarvisOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  railContext?: JarvisRailContext | null;
  railAppContext?: JarvisRailAppContext;
  onSeedPrompt?: (prompt: string) => void;
}

function HeaderChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-cyan-300/25 bg-cyan-400/10 px-2.5 py-1 text-[11px] font-medium text-cyan-100">
      {label}
    </span>
  );
}

export function JarvisOverlay({
  open,
  onOpenChange,
  children,
  railContext,
  railAppContext,
  onSeedPrompt,
}: JarvisOverlayProps) {
  const overlayScrollRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();
  const scopeMode = railContext?.scopeMode ?? railAppContext?.initialScopeMode ?? 'all_clients';
  const scopeChipLabel = scopeMode === 'client' ? 'Client scope' : 'All-clients scope';
  const quickPrompts = getJarvisQuickPrompts(scopeMode);
  const selectedClientLabel =
    railContext?.selectedClientName || railAppContext?.selectedClientName || 'selected client';
  const activeContextLabel =
    scopeMode === 'client' ? `Focused on ${selectedClientLabel}` : 'Focused on roster-wide intelligence';

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    overlayScrollRef.current?.scrollTo({ top: 0 });
    shellRef.current?.scrollTo({ top: 0 });
  }, [open]);

  return (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          ref={overlayScrollRef}
          className="fixed inset-0 z-[70] overflow-y-auto p-2 sm:p-4 lg:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0.16 : 0.24, ease: 'easeOut' }}
          aria-hidden={!open}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-slate-950/78 backdrop-blur-[8px]"
            aria-label="Close Agent overlay"
            onClick={() => onOpenChange(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0.14 : 0.22, ease: 'easeOut' }}
          />

          <motion.section
            ref={shellRef}
            role="dialog"
            aria-modal="true"
            aria-label="Coach OS Agent"
            className="relative mx-auto my-0 flex h-[calc(100dvh-1rem)] w-full max-w-[1380px] flex-col overflow-hidden rounded-[24px] border border-cyan-300/30 bg-[#060b14] shadow-[0_52px_130px_rgba(0,0,0,0.78)] ring-1 ring-white/14 sm:h-[calc(100dvh-2rem)] sm:rounded-[30px] lg:h-[calc(100dvh-3rem)] lg:rounded-[34px]"
            initial={{ opacity: 0, y: reduceMotion ? 4 : 16, scale: reduceMotion ? 1 : 0.986 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduceMotion ? 4 : 14, scale: reduceMotion ? 1 : 0.99 }}
            transition={{ duration: reduceMotion ? 0.18 : 0.3, ease: [0.22, 0.8, 0.28, 1] }}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.08),transparent_56%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))]" />

            <button
              type="button"
              aria-label="Close overlay"
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/[0.05] text-white/75 transition hover:bg-white/[0.1] hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>

            <motion.div
              className="relative z-10 border-b border-white/10 bg-gradient-to-r from-cyan-500/[0.09] via-transparent to-violet-500/[0.08] px-4 py-4 pr-14 sm:px-6 sm:py-5 sm:pr-16"
              initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduceMotion ? 0 : 0.03, duration: reduceMotion ? 0.14 : 0.24 }}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 grid h-10 w-10 place-items-center rounded-2xl border border-cyan-300/25 bg-cyan-400/15 text-cyan-300">
                    <BrainCircuit className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-200/80">
                      Command Center
                    </div>
                    <div className="mt-1 text-xl font-semibold text-white sm:text-2xl">Coach OS Agent</div>
                    <div className="mt-1 text-sm text-white/55">{activeContextLabel}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[`Overlay active`, scopeChipLabel, `Chat logic preserved`, `Context-aware rail`].map(
                    (label, idx) => (
                      <motion.div
                        key={label}
                        initial={{ opacity: 0, y: reduceMotion ? 0 : 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: reduceMotion ? 0 : 0.05 + idx * 0.03,
                          duration: reduceMotion ? 0.12 : 0.2,
                        }}
                      >
                        <HeaderChip label={label} />
                      </motion.div>
                    ),
                  )}
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative z-10 border-b border-white/10 px-4 py-3 sm:px-6"
              initial={{ opacity: 0, y: reduceMotion ? 0 : 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduceMotion ? 0 : 0.08, duration: reduceMotion ? 0.12 : 0.22 }}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100/75">
                  <WandSparkles className="h-3.5 w-3.5" />
                  Suggested Commands
                </div>
                <div className="text-xs text-white/45">Tap to seed prompt</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {quickPrompts.map((prompt) => (
                  <motion.button
                    key={prompt}
                    type="button"
                    whileHover={reduceMotion ? undefined : { y: -1, scale: 1.01 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.985 }}
                    onClick={() => onSeedPrompt?.(prompt)}
                    className="inline-flex items-center rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-cyan-50 transition duration-200 hover:border-cyan-300/40 hover:bg-cyan-400/12 hover:text-cyan-100 active:border-cyan-300/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/35"
                  >
                    <span className="text-left leading-tight">{prompt || 'Suggested prompt'}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden xl:flex-row"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: reduceMotion ? 0 : 0.12, duration: reduceMotion ? 0.14 : 0.24 }}
            >
              <motion.div
                className="flex min-h-0 flex-1 flex-col border-b border-white/10 bg-[#0b1321] xl:border-b-0 xl:border-r xl:border-white/10"
                initial={{ opacity: 0, x: reduceMotion ? 0 : -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: reduceMotion ? 0 : 0.12, duration: reduceMotion ? 0.14 : 0.24 }}
              >
                <div className="flex h-full min-h-0 flex-1 flex-col rounded-2xl bg-[#0a111d] p-1.5 sm:p-2.5">
                  {children}
                </div>
              </motion.div>
              {/* Right rail hidden temporarily — restore when layout is decided */}
            </motion.div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
